import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChartRendererService } from '@shared-lib/shared-services/chart-renderer.service';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { HostListener } from '@angular/core';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';


@Component({
  selector: 'app-widget-renderer',
  imports: [
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    CommonModule
  ],
  standalone: true,
  templateUrl: './widget-renderer.component.html',
  styleUrl: './widget-renderer.component.scss'
})
export class WidgetRendererComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() charts!: any
  @ViewChildren('chartContainer', { read: ViewContainerRef })
  chartContainers!: QueryList<ViewContainerRef>;
  subscription!: Subscription;
  isFullScreen = false;
  componentRef: any;


  constructor(
    private chartRendererService: ChartRendererService,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Listen for global filter changes to keep the three-dot menu in sync
    this.subscription = this.globalFilterService.filter$.subscribe((filters) => {
      // Detect global reset: no widgetId and changedFilterKey is null or absent
      const isGlobalReset = !filters?.widgetId &&
        (filters?.changedFilterKey === null || filters?.changedFilterKey === undefined);
      if (isGlobalReset) {
        // Directly reset the drill state on the child chart instance
        const instance = this.componentRef?.instance;
        if (instance && 'drillState' in instance) {
          instance.drillState = 'default';
        }
        // Delay change detection to let the chart finish re-rendering
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 300);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.chartContainers.length > 0) {
      const container = this.chartContainers.first;
      const chart = this.charts;
      if (chart) {
        this.componentRef = this.chartRendererService.renderChart(chart.type, container, chart);

        // Listen for internal toggleFullscreen events (e.g. from GenericTable menu)
        const instance = this.componentRef?.instance;
        if (instance && instance.toggleFullscreen) {
          instance.toggleFullscreen.subscribe(() => this.toggleFullScreen());
        }

        this.cdr.detectChanges();
      }
    }
  }

  @HostListener('window:keydown.escape')
  handleEscapeKey() {
    if (this.isFullScreen) {
      this.toggleFullScreen();
    }
  }

  toggleFullScreen() {
    // If we're closing fullscreen mode, trigger a data reset
    if (this.isFullScreen) {
      this.resetFilteredData();
    }

    this.isFullScreen = !this.isFullScreen;
    const isTableFullscreen = this.isFullScreen && this.isTableWidget;

    // Toggle scroll lock on root elements
    if (this.isFullScreen) {
      document.documentElement.classList.add('dvm-no-scroll');
      document.body.classList.add('dvm-no-scroll');
      document.body.classList.toggle('dvm-table-fullscreen', isTableFullscreen);
    } else {
      // Let the ResponsiveLayoutService handle restoring the correct global state 
      // (one-screen dash needs dvm-no-scroll back, scrollable does not)
      this.responsiveLayoutService.triggerReflow();
      document.body.classList.remove('dvm-no-scroll');
      document.body.classList.remove('dvm-table-fullscreen');
    }

    // Sync state to child component if it supports isFullScreen property
    const instance = this.componentRef?.instance;
    if (instance && 'isFullScreen' in instance) {
      instance.isFullScreen = this.isFullScreen;
    }

    // High performance multi-stage resize logic to wait for DOM and amCharts
    setTimeout(() => this.triggerResize(), 100);
    setTimeout(() => this.triggerResize(), 500);
    setTimeout(() => this.triggerResize(), 1200);
  }

  public downloadCsv(): void {
    const instance = this.componentRef?.instance;
    if (instance && typeof instance.downloadCsv === 'function') {
      instance.downloadCsv();
    }
  }

  public downloadImage(format: 'png' | 'jpeg'): void {
    const chart = this.getChartInstance();
    if (!chart || typeof chart.getDataURL !== 'function') {
      return;
    }

    const dataUrl = chart.getDataURL({
      type: format,
      pixelRatio: 2,
      backgroundColor: '#ffffff'
    });
    if (!dataUrl) {
      return;
    }

    const extension = format === 'jpeg' ? 'jpg' : 'png';
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${this.imageFileName()}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Drill Up: aggregate quarters → year bars
  public doDrillUp(): void {
    const instance = this.componentRef?.instance;
    if (instance && typeof instance.drillUp === 'function') instance.drillUp();
  }

  // Drill Down: repurposed to reset the chart back to default after Drill Up
  public doDrillDown(): void {
    this.doDrillReset();
  }

  // Restore: go back to the default year+quarter view
  public doDrillReset(): void {
    const instance = this.componentRef?.instance;
    if (instance && typeof instance.drillReset === 'function') {
      instance.drillReset();
    } else if (instance && typeof instance.drillDown === 'function') {
      instance.drillDown();
    }
  }

  get hasDrill(): boolean {
    const instance = this.componentRef?.instance;
    const hasDrillApi =
      !!instance &&
      typeof instance.drillUp === 'function' &&
      (typeof instance.drillReset === 'function' || typeof instance.drillDown === 'function');
    const chartConfig = this.charts?.chartConfig || {};
    const tooltipTimeView = String(chartConfig.tooltipTimeView || this.charts?.tooltipTimeView || '').toLowerCase();
    const apiUrl = String(this.charts?.apiUrl || this.charts?.mockDataUrl || '').toLowerCase();
    const widgetId = String(this.charts?.widgetId || '').toLowerCase();
    const isQuarterlyView =
      chartConfig.enableQuarterYearAxis === true ||
      this.charts?.enableQuarterYearAxis === true ||
      tooltipTimeView === 'quarterly' ||
      apiUrl.includes('view=quarterly') ||
      widgetId.includes('quarterly');

    return hasDrillApi && isQuarterlyView;
  }

  get drillState(): string {
    return this.componentRef?.instance?.drillState || 'default';
  }

  private getChartInstance(): { getDataURL?: (options: unknown) => string } | null {
    const instance = this.componentRef?.instance;
    if (!instance) {
      return null;
    }
    return instance.myChart || instance.chart || instance.chartInstance || instance.customChart || null;
  }

  private imageFileName(): string {
    const raw = String(this.charts?.title || this.charts?.widgetId || 'chart');
    const cleaned = raw.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
    return cleaned || 'chart';
  }

  private resetFilteredData(): void {
    const chart = this.charts;
    if (!chart) return;

    // 1. Reset WHOLE dashboard filters (as requested: "reset button of whole screen")
    this.globalFilterService.clearAllFilters();

    // 2. Clear visual state on the chart instance if supported
    const instance = this.componentRef?.instance;
    if (!instance) return;

    // Standard way to clear all local component selections
    if (typeof instance.reset === 'function') {
      instance.reset();
    } else {
      // Fallback for components that haven't implemented reset() yet
      if (typeof instance.resetCategoryHighlight === 'function') {
        instance.resetCategoryHighlight();
      }
      if (typeof instance.resetSeriesHighlight === 'function') {
        instance.resetSeriesHighlight();
      }
      if ('currentlySelectedIndices' in instance) {
        instance.currentlySelectedIndices = [];
      }
      if ('selectedLegends' in instance) {
        instance.selectedLegends = [];
      }
      if ('selectedCategories' in instance) {
        instance.selectedCategories = [];
      }
    }
  }

  private triggerResize(): void {
    const instance = this.componentRef?.instance;
    if (instance) {
      if (instance.myChart && typeof instance.myChart.resize === 'function') {
        instance.myChart.resize();
      } else if (typeof instance.resize === 'function') {
        instance.resize();
      } else {
        window.dispatchEvent(new Event('resize'));
      }
    }
  }

  get hasCsvExport(): boolean {
    const instance = this.componentRef?.instance;
    return !!(instance && typeof instance.downloadCsv === 'function');
  }

  get isTableWidget(): boolean {
    const type = this.charts?.type;
    return type === 'generic-table' || type === 'table-generic';
  }

  get isCustomChart(): boolean {
    const type = this.charts?.type;
    return type === 'generic-stack-bar-chart' ||
      type === 'stack-bar-chart-generic' ||
      type === 'mbar-mline' ||
      type === 'barline' ||
      type === 'bar_line' ||
      type === 'bar-line' ||
      type === 'generic-table' ||
      type === 'table-generic' ||
      type === 'bubble-chart' ||
      type === 'pie-chart' ||
      type === 'donut-chart' ||
      type === 'bar-chart' ||
      type === 'line-chart' ||
      type === 'stacked-area-chart' ||
      type === 'waterfall-chart' ||
      type === 'quad-bubble-chart' ||
      type === 'treemap-chart' ||
      type === 'line-column-chart' ||
      type === 'combined-chart' ||
      type === 'waterfall-stack';
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Ensure scroll lock is removed if component is destroyed
    document.documentElement.classList.remove('dvm-no-scroll');
    document.body.classList.remove('dvm-no-scroll');
    document.body.classList.remove('dvm-table-fullscreen');
  }
}

